// INFORMAR DADOS DO CONTRATO LEGADO — feature 012.
//
// O legado nasce do tombamento só com o que a folha diz: parcela e prazo. A
// Atlas não tem a cédula; o credor tem. Este modal é por onde ele informa o
// valor solicitado e a taxa a.m. — e por onde a averbadora o faz como exceção,
// com motivo. O mesmo componente serve aos dois: muda quem chama e se o motivo
// é obrigatório.
//
// O que a tela deriva na hora, pelo MESMO `calcCET` do simulador: CET, IOF,
// líquido e a parcela teórica. A parcela da folha NÃO muda — se a teórica não
// bater com ela, isso é informação (a cédula tem tarifa e seguro que a Atlas
// não vê), nunca bloqueio.

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiHttpError } from "@atlas/sdk";
import {
  Button,
  CurrencyField,
  FormActions,
  FormGrid,
  NumberField,
  TextareaField,
  TextField,
} from "@atlas/ui/web";
import { calcCET, parcelaInformadaBate } from "@atlas/domain";
import { atlas } from "../lib/sdk";
import { rotuloInformadoPor, type DadosInformadosDoContrato } from "../lib/dados-informados";

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function InformarDadosLegadoModal({
  adf,
  nome,
  valorParcela,
  totalParcelas,
  atual,
  papel,
  onClose,
  onSalvo,
}: {
  adf: string;
  nome: string;
  /** Parcela e prazo DA FOLHA — mostrados, nunca editados aqui. */
  valorParcela: number;
  totalParcelas: number;
  /** O informe vigente, quando é correção. */
  atual?: DadosInformadosDoContrato | null;
  papel: "banco" | "averbadora";
  onClose: () => void;
  /** Chamado depois de gravar — quem abriu invalida as queries. */
  onSalvo: () => void;
}) {
  const [valor, setValor] = useState<number | null>(atual?.valorSolicitado ?? null);
  const [taxaPct, setTaxaPct] = useState<number>(atual ? Number((atual.taxaAm * 100).toFixed(4)) : 0);
  const [dataContrato, setDataContrato] = useState<string>(atual?.dataContrato ?? "");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  /** Avisos que a API devolveu ao gravar (teto do convênio). Gravou mesmo assim;
   *  ficam na tela até o operador fechar, senão ninguém os lê. */
  const [avisos, setAvisos] = useState<string[] | null>(null);

  const taxaFracao = Number((taxaPct / 100).toFixed(6));
  const derivado = useMemo(() => {
    if (!valor || valor <= 0 || taxaFracao <= 0 || totalParcelas <= 0) return null;
    try {
      const r = calcCET({ valor, parcelas: totalParcelas, taxaMensal: taxaFracao });
      return {
        parcela: Math.round(r.parcela * 100) / 100,
        iof: Math.round(r.iof * 100) / 100,
        liquido: Math.round(r.valorLiquido * 100) / 100,
        cetPct: r.mensal * 100,
      };
    } catch {
      return null;
    }
  }, [valor, taxaFracao, totalParcelas]);
  const parcelaBate = derivado ? parcelaInformadaBate(derivado.parcela, valorParcela) : null;

  const motivoFalta = papel === "averbadora" && motivo.trim().length < 10;
  const podeSalvar = !!valor && valor > 0 && taxaFracao > 0 && !motivoFalta;

  const mut = useMutation({
    mutationFn: () => {
      const corpo = { valorSolicitado: valor!, taxaAm: taxaFracao, dataContrato: dataContrato || undefined };
      return papel === "banco"
        ? atlas.banco.informarDadosLegado(adf, corpo)
        : atlas.admin.informarDadosLegado(adf, { ...corpo, motivo: motivo.trim() });
    },
    onSuccess: (r) => {
      onSalvo();
      if (r.avisos.length > 0) setAvisos(r.avisos);
      else onClose();
    },
    onError: (e) => {
      // O envelope de validação traz o campo; a mensagem genérica "Dados
      // invalidos" sozinha não diz o que corrigir.
      if (e instanceof ApiHttpError) {
        const det = (e as ApiHttpError & { details?: unknown }).details;
        const campos =
          det && typeof det === "object"
            ? Object.values(det as Record<string, unknown>).filter((v) => typeof v === "string")
            : [];
        setErro(campos.length ? campos.join(" ") : e.message);
      } else {
        setErro(e instanceof Error ? e.message : "Falha ao gravar.");
      }
    },
  });

  return (
    <div
      style={backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !mut.isPending) onClose();
      }}
    >
      <div style={card} onMouseDown={(e) => e.stopPropagation()}>
        <div>
          <h3 style={{ margin: 0 }}>{atual ? "Corrigir dados do contrato" : "Informar dados do contrato"}</h3>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            {nome} · ADF <code style={{ fontFamily: "var(--font-mono)" }}>{adf}</code>
          </div>
        </div>

        {avisos ? (
          <>
            <div style={aviso("emerald")}>
              Dados gravados. {atual ? "O informe anterior fica no histórico." : ""}
            </div>
            <div style={aviso("gold")}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Avisos</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
            <FormActions>
              <Button type="button" onClick={onClose}>
                Fechar
              </Button>
            </FormActions>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
              Este contrato já estava na folha quando a Atlas chegou, e a Atlas não tem a cédula. Informe o
              que está nela: o <b>valor solicitado</b> e a <b>taxa a.m.</b>. A parcela e o prazo continuam
              vindo da folha — não mudam aqui.
            </div>

            {atual ? (
              <div style={aviso("gold")}>
                Já {rotuloInformadoPor(atual)}: {BRL.format(atual.valorSolicitado)} @{" "}
                {(atual.taxaAm * 100).toFixed(2)}% a.m.
                {atual.porNome ? ` (${atual.porNome})` : ""}. Gravar de novo substitui; o anterior fica no
                histórico.
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 10,
              }}
            >
              <Caixa rotulo="Parcela em folha" valor={BRL.format(valorParcela)} />
              <Caixa rotulo="Total de parcelas" valor={`${totalParcelas}×`} />
              <Caixa
                rotulo="Total em folha"
                valor={BRL.format(Math.round(valorParcela * totalParcelas * 100) / 100)}
              />
            </div>

            <FormGrid cols={3}>
              <CurrencyField
                label="Valor solicitado"
                required
                value={valor}
                onValueChange={(v) => {
                  setValor(v);
                  setErro(null);
                }}
                hint="O principal da cédula, sem juros."
              />
              <NumberField
                label="Taxa a.m. (%)"
                required
                value={taxaPct || ""}
                step="0.01"
                min={0}
                onChange={(e) => {
                  setTaxaPct(Number(e.target.value) || 0);
                  setErro(null);
                }}
                hint="Ex.: 1,89"
              />
              <TextField
                label="Data do contrato"
                type="date"
                value={dataContrato}
                onChange={(e) => setDataContrato(e.target.value)}
                hint="Opcional"
              />
            </FormGrid>

            {derivado ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 10,
                }}
              >
                <Caixa rotulo="CET a.m." valor={`${derivado.cetPct.toFixed(2)}%`} />
                <Caixa rotulo="IOF" valor={BRL.format(derivado.iof)} />
                <Caixa rotulo="Líquido" valor={BRL.format(derivado.liquido)} />
                <Caixa
                  rotulo="Parcela calculada"
                  valor={BRL.format(derivado.parcela)}
                  cor={parcelaBate ? "var(--emerald-500)" : "var(--gold-500)"}
                />
              </div>
            ) : null}
            {derivado && parcelaBate === false ? (
              <div style={aviso("gold")}>
                Com esses números a parcela seria {BRL.format(derivado.parcela)}; a folha desconta{" "}
                {BRL.format(valorParcela)}. É comum — a cédula pode ter tarifa ou seguro que a Atlas não vê. A
                parcela da folha continua valendo; isto é só informação.
              </div>
            ) : null}

            {papel === "averbadora" ? (
              <TextareaField
                label="Motivo"
                required
                hint="Quem informa é o credor. Diga por que a averbadora está informando (credor sem acesso, correção…)."
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={3}
                maxLength={500}
              />
            ) : null}

            {erro ? <div style={aviso("danger")}>{erro}</div> : null}

            <FormActions>
              <Button variant="ghost" type="button" onClick={onClose} disabled={mut.isPending}>
                Cancelar
              </Button>
              <Button type="button" onClick={() => mut.mutate()} disabled={!podeSalvar || mut.isPending}>
                {mut.isPending ? "Gravando…" : atual ? "Gravar correção" : "Gravar dados"}
              </Button>
            </FormActions>
          </>
        )}
      </div>
    </div>
  );
}

function Caixa({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 10,
        background: "var(--bg-elev-2)",
        border: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: "var(--text-dim)",
        }}
      >
        {rotulo}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, color: cor ?? "var(--text)" }}>{valor}</div>
    </div>
  );
}

function aviso(tom: "gold" | "danger" | "emerald"): React.CSSProperties {
  const cor =
    tom === "gold" ? "var(--gold-500)" : tom === "danger" ? "var(--danger-500)" : "var(--emerald-500)";
  return {
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid color-mix(in srgb, ${cor} 50%, transparent)`,
    background: `color-mix(in srgb, ${cor} 10%, transparent)`,
    fontSize: 13,
    lineHeight: 1.5,
  };
}

// Mesma escala do modal de proposta do banco (zIndex 100).
const backdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(10,22,40,.6)",
  display: "grid",
  placeItems: "center",
  zIndex: 100,
  backdropFilter: "blur(6px)",
};
const card: React.CSSProperties = {
  background: "var(--bg-elev)",
  border: "1px solid var(--border-strong)",
  borderRadius: 14,
  padding: 24,
  maxWidth: 720,
  width: "calc(100% - 48px)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  boxShadow: "var(--shadow-lg)",
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};
